import { createSlice } from '@reduxjs/toolkit'
import { AnyAction } from 'redux'

import { NAMESPACE } from '../constants'
import { TApiError, TApiStateAction, TApiStateState, THttpErrorAction } from '../types'

const initialState: TApiStateState = {
  loading: {},
  errors: {},
}

/**
 * Every action an API module mints — `<name>_request`, `<name>_success` and
 * `<name>_error` — plus the global `http_error`, which matches as
 * `['http_error', 'http', 'error']`.
 */
const API_ACTION = /(.*)_(request|success|error)/

const GENERAL_ERROR_KEY = 'general'

/**
 * `userApi/login_request` -> `userApi/login`. Both `loading` and `errors` are
 * keyed this way, so a `_success` or an `http_error` lands on the same key as
 * the `_request` that started it.
 */
export const requestKey = (type: string): string => type.replace('_request', '')

const isApiStateAction = (action: AnyAction): action is TApiStateAction =>
  typeof action.type === 'string' && API_ACTION.test(action.type)

const isHttpError = (action: TApiStateAction): action is THttpErrorAction => action.type === 'http_error'

const slice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addMatcher(isApiStateAction, (state, action) => {
      if (isHttpError(action) && action.meta?.initAction) {
        const { initAction, reduceByPayload } = action.meta
        const actionKey = requestKey(initAction.type)
        state.loading[actionKey] = false

        const response = action.payload?.response
        // `errors` is Phoenix's shape, `detail` the odd Phoenix one-liner,
        // `message` is le-go's (echo HTTPError).
        const errors: TApiError[] = response?.errors
          ? response.errors
          : response?.detail
          ? [{ key: GENERAL_ERROR_KEY, message: response.detail }]
          : response?.message
          ? [{ key: GENERAL_ERROR_KEY, message: response.message }]
          : [{ key: GENERAL_ERROR_KEY, message: 'An unknown error occured.' }]

        if (reduceByPayload) {
          const stored = state.errors[actionKey]
          /**
           * A given key is only ever written one way, so an array here would
           * mean the same request was dispatched both with and without
           * `reduceByPayload`.
           */
          const byPayload = stored != null && !Array.isArray(stored) ? stored : {}
          byPayload[JSON.stringify(initAction.payload)] = errors
          state.errors[actionKey] = byPayload
        } else {
          state.errors[actionKey] = errors
        }
        return
      }

      /**
       * An `http_error` with no `initAction` falls through to here and sets
       * `loading.http` to false, which nothing reads.
       */
      const matches = API_ACTION.exec(action.type)
      if (!matches) return

      const [, requestName, requestState] = matches
      state.loading[requestName] = requestState === 'request'
    })
  },
})

const { reducer } = slice

export { initialState, reducer }
